import {
  Avatar,
  Box,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getPersonalDetails } from 'API/personalDetails';
import { RequestsContext } from 'Context';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import {
  useAppSelector,
  useDictionary,
  useDispatchApp,
  useIsDesktop,
} from 'Hooks';
import { AccordionArrowPurple } from 'Images';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { illegalChars, notDigits, requiredFieldMessage } from 'Regex';
import { setPersonalDetails } from 'Store/PersonalDetailsSlice';

const maxInputLength = 500;

export const Step2 = (props: any) => {
  const { onPhoneError } = props;
  const dictionary = useDictionary(Dictionary, DICTIONARY.Requests);
  const isDesktop = useIsDesktop();
  const [phonePrefix, setPhonePrefix] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [note, setNote] = useState('');
  const [phonePrefixError, setPhonePrefixError] = useState(false);
  const { setRequestsData, requestsData } = useContext(RequestsContext);

  const MIN_PHONE_NUMBER_LENGTH = 7;

  const handlePrefixChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPhonePrefix(e.target.value);
    setPhonePrefixError(false);
    if (phoneNumber == '' || phoneNumber.length < MIN_PHONE_NUMBER_LENGTH) {
      onPhoneError(true);
    } else {
      setPhonePrefixError(false);
      onPhoneError(false);
    }
  };

  const handleChangePhone = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const phoneValue = e.target.value.replace(/[^\d]/g, '');
    setPhoneNumber(phoneValue);
    if (phonePrefix === '' && phoneValue === '') {
      setPhonePrefixError(false);
      onPhoneError(false);
    } else if (phoneValue !== '' && phonePrefix === '') {
      setPhonePrefixError(true);
      onPhoneError(true);
    } else if (phoneValue === '' && phonePrefix !== '') {
      onPhoneError(true);
    } else if (phoneValue.length < MIN_PHONE_NUMBER_LENGTH) {
      onPhoneError(true);
    } else {
      setPhonePrefixError(false);
      onPhoneError(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setRequestsData((prevRequestData) => {
        const clonePrevRequestData = { ...prevRequestData };
        clonePrevRequestData.phoneNumber = phoneNumber;

        return clonePrevRequestData;
      });
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
  }, [phoneNumber]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setRequestsData((prevRequestData) => {
        const clonePrevRequestData = { ...prevRequestData };
        clonePrevRequestData.phonePrefix = phonePrefix;

        return clonePrevRequestData;
      });
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
  }, [phonePrefix]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setRequestsData((prevRequestData) => {
        const clonePrevRequestData = { ...prevRequestData };
        clonePrevRequestData.note = note;

        return clonePrevRequestData;
      });
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
  }, [note]);

  const generalOptions = useAppSelector(
    (generalOptions) => generalOptions.generalOptions.generalOptions
  );

  const personalDetails = useAppSelector(
    ({ personalDetails: { personalDetails } }) => personalDetails
  );

  const dispatch = useDispatchApp();

  const initializePersonalDetails = useCallback(async () => {
    const result = await getPersonalDetails();
    if (result) {
      dispatch(setPersonalDetails(result));
    }
  }, [dispatch]);

  useEffect(() => {
    if (personalDetails === null) initializePersonalDetails();
  }, [initializePersonalDetails, personalDetails]);

  useEffect(() => {
    setPhonePrefix(
      (
        requestsData.requestDataJson.contactDetails?.phonePrefix ||
        personalDetails?.phones.find((phone) => phone.phoneType === 3)
          ?.prefixId ||
        personalDetails?.mainPhonePrefix ||
        ''
      ).toString()
    );
    setPhoneNumber(
      requestsData.requestDataJson.contactDetails?.phoneNumber ||
        personalDetails?.phones.find((phone) => phone.phoneType === 3)
          ?.phoneNum ||
        personalDetails?.mainPhone ||
        ''
    );
  }, [
    personalDetails?.mainPhone,
    personalDetails?.mainPhonePrefix,
    personalDetails?.phones,
    requestsData.requestDataJson.contactDetails?.phoneNumber,
    requestsData.requestDataJson.contactDetails?.phonePrefix,
  ]);

  return (
    <Stack gap={'16px'} maxWidth={'400px'} m={'0 auto'}>
      <Typography component={'label'} htmlFor={'input-notes'} variant={'body1'}>
        {dictionary.Step2.requestNotes}
      </Typography>
      <Stack gap={isDesktop ? '24px' : '16px'} zIndex={0}>
        <Box position={'relative'}>
          <TextField
            id='input-notes'
            multiline
            rows={5}
            sx={{
              width: '100%',
              borderRadius: '8px',
              '.MuiOutlinedInput-root': {
                padding: '16px 20px',
                fontSize: '18px',
              },
              '&& .MuiOutlinedInput-notchedOutline': {
                bgcolor: 'white',
                zIndex: '-1',
                borderRadius: '8px',
                borderColor: 'primary.shade_200',
              },
              '&& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderRadius: '8px',
                borderColor: 'primary.main',
                borderWidth: '1px',
              },
              '& ::placeholder': { maxWidth: '32ch' },
            }}
            placeholder={dictionary.Step2.requestNotesPlaceholder}
            onChange={(e) => {
              if (!props.isTouched) props.setIsTouched(true);

              setNote(e.target.value);
              setRequestsData((prevData) => ({
                ...prevData,
                note: e.target.value,
              }));
            }}
            onInput={(e: any) => {
              e.target.value = e.target.value.replace(illegalChars, '');
            }}
            inputProps={{ maxLength: maxInputLength }}
            error={props.isTouched && props.isNoteError}
            helperText={
              props.isTouched && props.isNoteError && requiredFieldMessage
            }
            FormHelperTextProps={{ error: true, sx: { fontSize: '14px' } }}
          />
          <Typography
            color='purple2.main'
            variant='body7'
            sx={{ position: 'absolute', top: '118px', right: '20px' }}
          >{`${note.length}/${maxInputLength}`}</Typography>
        </Box>
        <Stack gap={'16px'}>
          <Typography sx={{ fontSize: '16px' }}>
            {dictionary.Step2.phoneNumber}
          </Typography>
          <Grid
            container
            item
            component='fieldset'
            sx={{
              flexDirection: 'row-reverse',
              justifyContent: 'flex-end',
              flexWrap: 'nowrap',
              p: '0',
              borderRadius: '6px',
              '& .MuiOutlinedInput-notchedOutline': {
                bgcolor: 'white',
                zIndex: -1,
                borderRadius: '6px',
                borderColor: 'primary.shade_200',
              },
              '&& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderRadius: '6px',
                borderWidth: '1px',
                borderColor: 'primary.main',
              },
              '.MuiOutlinedInput-root': {
                minHeight: '48px',
                height: '48px',
                alignItems: 'center',
                mt: '5px',
              },
            }}
          >
            <legend>{dictionary.Step2.phoneNumber}</legend>
            {generalOptions && (
              <TextField
                id='select-phone-prefix'
                select
                value={phonePrefix}
                variant='outlined'
                onChange={handlePrefixChange}
                error={phonePrefixError}
                helperText={
                  phonePrefixError && dictionary.Step2.phonePrefixError
                }
                label={dictionary.Step2.prefix}
                sx={{ width: '108px', ml: '16px' }}
                SelectProps={{
                  MenuProps: {
                    sx: {
                      maxHeight: '244px',
                      boxShadow: '0px 6px 21px rgba(45, 112, 217, 0.15)',
                    },
                    MenuListProps: {
                      sx: {
                        '& :hover': { bgcolor: 'primary.shade_100' },
                      },
                    },
                    PaperProps: { sx: { borderRadius: '6px' } },
                  },
                  IconComponent: () => {
                    return (
                      <Avatar
                        alt=''
                        src={AccordionArrowPurple}
                        sx={{
                          width: '32px',
                          height: '32px',
                          position: 'absolute',
                          right: '7px',
                        }}
                      />
                    );
                  },
                }}
              >
                <MenuItem key={'empty'} value={''}>
                  {dictionary.prefix}
                </MenuItem>
                {generalOptions?.general?.phonePrefixes &&
                  generalOptions.general.phonePrefixes?.map((prefix) => (
                    <MenuItem key={prefix.id} value={prefix.id}>
                      <Typography variant={'body3'}>{prefix.value}</Typography>
                    </MenuItem>
                  ))}
              </TextField>
            )}
            <TextField
              id='input-phone-number'
              label={dictionary.Step2.phone}
              value={phoneNumber}
              onChange={(e) => handleChangePhone(e)}
              onInput={(e: any) => {
                const newValue = e.target.value.replace(notDigits, '');
                e.target.value = newValue;
              }}
              type='tel'
              inputProps={{
                maxLength: 7,
              }}
              sx={{
                mr: '5px',
                width: isDesktop ? '282px' : '63%',
              }}
              error={phoneNumber.length !== 0 && !/^\d{7}$/.test(phoneNumber)}
              helperText={
                phoneNumber.length !== 0 && !/^\d{7}$/.test(phoneNumber)
                  ? dictionary.Step2.errorPhone
                  : ''
              }
            />
          </Grid>
        </Stack>
      </Stack>
    </Stack>
  );
};
